import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { Clock, EyeOff, Flame, Repeat, RotateCcw, Save, Sliders } from 'lucide-react';
import {
  api,
  canManageUsers,
  type AuthUser,
  type Candidate,
  type ScorePolicyDto,
} from '@/lib/api';
import {
  DEFAULT_SCORE_POLICY,
  type ScorePolicy,
  formatScore,
  scoreBreakdown,
} from '@/lib/selectionScore';
import { formatPower } from '@/lib/utils';
import { PlayerFlagChip } from '@/components/PlayerFlagChip';
import { RecentEventsChip } from '@/components/RecentEventsChip';

type PercentField = keyof Pick<
  ScorePolicy,
  | 'notPickedBonusPerStreak'
  | 'notPickedMaxBonus'
  | 'notRegisteredBonusPerStreak'
  | 'notRegisteredMaxBonus'
  | 'noShowPenaltyPerStreak'
  | 'noShowMaxPenalty'
  | 'latePenaltyPerStreak'
  | 'lateMaxPenalty'
  | 'subsOnlyBonusPerStreak'
  | 'subsOnlyMaxBonus'
>;
type FactorField = keyof Pick<
  ScorePolicy,
  'yellowCardFactor' | 'greenFlagFactor' | 'csSlotPreferenceFactor'
>;

type FieldGroup = {
  title: string;
  hint: string;
  rows: {
    key: PercentField | FactorField;
    label: string;
    kind: 'percent' | 'factor';
    hint?: string;
  }[];
};

const GROUPS: FieldGroup[] = [
  {
    title: 'Not-picked streak',
    hint: 'Boost applied per consecutive event the player applied for but was not picked.',
    rows: [
      { key: 'notPickedBonusPerStreak', label: 'Bonus per streak', kind: 'percent' },
      { key: 'notPickedMaxBonus', label: 'Max total bonus', kind: 'percent' },
    ],
  },
  {
    title: 'Not-registered streak (grey)',
    hint: 'Softer boost for players who did not sign up for recent events. Should stay smaller than the not-picked bonus.',
    rows: [
      { key: 'notRegisteredBonusPerStreak', label: 'Bonus per streak', kind: 'percent' },
      { key: 'notRegisteredMaxBonus', label: 'Max total bonus', kind: 'percent' },
    ],
  },
  {
    title: 'Long-running subs',
    hint: 'Gentle boost for players stuck on the sub bench, so they eventually get promoted to starter.',
    rows: [
      { key: 'subsOnlyBonusPerStreak', label: 'Bonus per streak', kind: 'percent' },
      { key: 'subsOnlyMaxBonus', label: 'Max total bonus', kind: 'percent' },
    ],
  },
  {
    title: 'No-show streak',
    hint: 'Penalty per consecutive no-show — subtracted from the streak factor.',
    rows: [
      { key: 'noShowPenaltyPerStreak', label: 'Penalty per streak', kind: 'percent' },
      { key: 'noShowMaxPenalty', label: 'Max total penalty', kind: 'percent' },
    ],
  },
  {
    title: 'Late streak',
    hint: 'Penalty per consecutive late attendance.',
    rows: [
      { key: 'latePenaltyPerStreak', label: 'Penalty per streak', kind: 'percent' },
      { key: 'lateMaxPenalty', label: 'Max total penalty', kind: 'percent' },
    ],
  },
  {
    title: 'Flag multipliers',
    hint: 'Applied on top of the streak factor. Yellow softens the score; green sharply boosts. Two yellow cards elevate to red (excluded).',
    rows: [
      { key: 'yellowCardFactor', label: 'Yellow card ×', kind: 'factor' },
      { key: 'greenFlagFactor', label: 'Green flag ×', kind: 'factor' },
      { key: 'csSlotPreferenceFactor', label: 'CS slot preference ×', kind: 'factor' },
    ],
  },
];

function toPolicy(dto: ScorePolicyDto): ScorePolicy {
  return {
    notPickedBonusPerStreak: dto.notPickedBonusPerStreak,
    notPickedMaxBonus: dto.notPickedMaxBonus,
    notRegisteredBonusPerStreak: dto.notRegisteredBonusPerStreak,
    notRegisteredMaxBonus: dto.notRegisteredMaxBonus,
    noShowPenaltyPerStreak: dto.noShowPenaltyPerStreak,
    noShowMaxPenalty: dto.noShowMaxPenalty,
    latePenaltyPerStreak: dto.latePenaltyPerStreak,
    lateMaxPenalty: dto.lateMaxPenalty,
    subsOnlyBonusPerStreak: dto.subsOnlyBonusPerStreak,
    subsOnlyMaxBonus: dto.subsOnlyMaxBonus,
    yellowCardFactor: dto.yellowCardFactor,
    greenFlagFactor: dto.greenFlagFactor,
    csSlotPreferenceFactor: dto.csSlotPreferenceFactor,
  };
}

function policiesEqual(a: ScorePolicy, b: ScorePolicy): boolean {
  return (Object.keys(a) as (keyof ScorePolicy)[]).every((k) => a[k] === b[k]);
}

function LeaveBadge({ candidate }: { candidate: Candidate }) {
  const leave = candidate.player.currentLeave;
  if (!leave) return null;
  const range = leave.endsAt ? `${leave.startsAt} – ${leave.endsAt}` : `from ${leave.startsAt}`;
  const title = leave.reason ? `On leave (${range}) — ${leave.reason}` : `On leave (${range})`;
  return (
    <span
      title={title}
      className="inline-flex items-center rounded bg-teal-900/50 px-1 py-0.5 text-[10px] font-semibold text-teal-200"
    >
      🌴
    </span>
  );
}

function StreakBadges({ candidate }: { candidate: Candidate }) {
  const m = candidate.metrics;
  return (
    <>
      {m.consecutiveNotPicked >= 1 && (
        <span
          title={`Not picked ${m.consecutiveNotPicked}× in a row — prioritise`}
          className="inline-flex items-center gap-0.5 rounded bg-orange-900/50 px-1 py-0.5 text-[10px] font-semibold text-orange-300"
        >
          <Flame size={11} />
          {m.consecutiveNotPicked}
        </span>
      )}
      {(m.consecutiveNotRegistered ?? 0) >= 1 && (
        <span
          title={`Did not register for the last ${m.consecutiveNotRegistered} events`}
          className="inline-flex items-center gap-0.5 rounded bg-slate-800/70 px-1 py-0.5 text-[10px] font-semibold text-slate-300"
        >
          <EyeOff size={11} />
          {m.consecutiveNotRegistered}
        </span>
      )}
      {m.consecutiveNoShows >= 1 && (
        <span
          title={`${m.consecutiveNoShows} no-shows in a row`}
          className="inline-flex items-center gap-0.5 rounded bg-red-900/50 px-1 py-0.5 text-[10px] font-semibold text-red-300"
        >
          🚫{m.consecutiveNoShows}
        </span>
      )}
      {m.consecutiveLates >= 1 && (
        <span
          title={`Late ${m.consecutiveLates}× in a row`}
          className="inline-flex items-center gap-0.5 rounded bg-amber-900/50 px-1 py-0.5 text-[10px] font-semibold text-amber-300"
        >
          <Clock size={11} />
          {m.consecutiveLates}
        </span>
      )}
      {m.consecutiveSubsOnly >= 1 && (
        <span
          title={`Sub-only for ${m.consecutiveSubsOnly} events — nudge to starter`}
          className="inline-flex items-center gap-0.5 rounded bg-sky-900/50 px-1 py-0.5 text-[10px] font-semibold text-sky-300"
        >
          <Repeat size={11} />
          {m.consecutiveSubsOnly}
        </span>
      )}
    </>
  );
}

export default function AdminScorePolicy({ user }: { user: AuthUser }) {
  if (!canManageUsers(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <AdminScorePolicyInner />;
}

function AdminScorePolicyInner() {
  const qc = useQueryClient();
  const policyQuery = useQuery({
    queryKey: ['score-policy'],
    queryFn: api.getScorePolicy,
  });
  const nextEventQuery = useQuery({
    queryKey: ['events', 'next-for-preview'],
    queryFn: async () => {
      const list = await api.listEvents();
      return list.find((e) => e.status === 'planned') ?? list[0] ?? null;
    },
  });
  const candidatesQuery = useQuery({
    queryKey: ['score-policy-candidates', nextEventQuery.data?.id],
    queryFn: () => api.candidates(nextEventQuery.data!.id, 'A'),
    enabled: !!nextEventQuery.data?.id,
  });

  const [draft, setDraft] = useState<ScorePolicy | null>(null);
  useEffect(() => {
    if (policyQuery.data && draft === null) {
      setDraft(toPolicy(policyQuery.data));
    }
  }, [policyQuery.data, draft]);

  const server = policyQuery.data ? toPolicy(policyQuery.data) : DEFAULT_SCORE_POLICY;
  const active = draft ?? server;
  const dirty = draft ? !policiesEqual(draft, server) : false;

  const save = useMutation({
    mutationFn: (p: ScorePolicy) => api.updateScorePolicy(p),
    onSuccess: (row) => {
      qc.setQueryData(['score-policy'], row);
      qc.invalidateQueries({ queryKey: ['candidates'] });
      setDraft(toPolicy(row));
    },
  });

  const preview = useMemo(() => {
    const list = candidatesQuery.data ?? [];
    const m1Ranks = new Map<number, number>();
    [...list]
      .sort((a, b) => (b.latestSnapshot?.m1Power ?? 0) - (a.latestSnapshot?.m1Power ?? 0))
      .forEach((c, i) => m1Ranks.set(c.player.id, i + 1));
    return [...list]
      .map((c) => ({ c, bd: scoreBreakdown(c, active), m1Rank: m1Ranks.get(c.player.id) ?? 0 }))
      .sort((a, b) => b.bd.score - a.bd.score);
  }, [candidatesQuery.data, active]);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
      <section className="space-y-4">
        <header className="flex items-center gap-2">
          <Sliders size={16} className="text-amber-300" />
          <h1 className="text-lg font-semibold">Score policy</h1>
        </header>
        <p className="text-xs text-slate-400">
          Adjust the weights that drive candidate scoring. Changes on this page
          preview immediately in the list on the right. Nothing is persisted
          until you press Save.
        </p>

        {GROUPS.map((g) => (
          <div key={g.title} className="rounded border border-slate-800 bg-slate-950/40 p-3">
            <h2 className="text-sm font-semibold text-slate-100">{g.title}</h2>
            <p className="mb-2 text-[11px] text-slate-500">{g.hint}</p>
            <div className="grid gap-2">
              {g.rows.map((r) => (
                <PolicyField
                  key={r.key}
                  label={r.label}
                  kind={r.kind}
                  value={active[r.key]}
                  onChange={(v) =>
                    setDraft((prev) => ({ ...(prev ?? server), [r.key]: v }))
                  }
                />
              ))}
            </div>
          </div>
        ))}

        <div className="sticky bottom-2 flex items-center justify-between gap-2 rounded border border-slate-800 bg-slate-950/80 px-3 py-2 backdrop-blur">
          <span className="text-xs text-slate-400">
            {policyQuery.data?.updatedByName && policyQuery.data.updatedAt
              ? `Last change: ${policyQuery.data.updatedAt.slice(0, 16).replace('T', ' ')} · ${policyQuery.data.updatedByName}`
              : 'No changes recorded yet'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setDraft(server)}
              disabled={!dirty || save.isPending}
              className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs disabled:opacity-40"
            >
              <RotateCcw size={12} /> Discard
            </button>
            <button
              onClick={() => draft && save.mutate(draft)}
              disabled={!dirty || save.isPending}
              className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-2 py-1 text-xs font-semibold text-emerald-100 hover:bg-emerald-800/60 disabled:opacity-40"
            >
              <Save size={12} />
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        {save.isError && (
          <div className="rounded bg-red-900/40 px-2 py-1 text-xs text-red-200">
            {(save.error as Error)?.message ?? 'Save failed'}
          </div>
        )}
      </section>

      <section>
        <header className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Live preview</h2>
          <span className="text-xs text-slate-500">
            {nextEventQuery.data
              ? `Based on ${nextEventQuery.data.type} · ${nextEventQuery.data.eventDate}`
              : 'No event available'}
          </span>
        </header>
        {candidatesQuery.isLoading && <p className="text-xs text-slate-500">Loading…</p>}
        {preview.length === 0 && !candidatesQuery.isLoading && (
          <p className="text-xs text-slate-500">
            Create a planned event to see live-updated rankings here.
          </p>
        )}
        <ol className="space-y-1">
          {preview.map(({ c, bd, m1Rank }, i) => (
            <li
              key={c.player.id}
              className={
                'flex flex-wrap items-center gap-2 rounded px-2 py-1 text-sm ' +
                (bd.redCarded
                  ? 'bg-red-950/40'
                  : bd.forced
                    ? 'bg-amber-950/30'
                    : bd.softForce
                      ? 'bg-emerald-950/30'
                      : 'bg-slate-900/60')
              }
            >
              <span className="w-6 text-xs tabular-nums text-slate-500">{i + 1}</span>
              <span className="min-w-0 truncate">
                {c.player.displayName?.trim() || c.player.name}
              </span>
              <span className="text-[10px] text-slate-500">{c.player.rank}</span>
              <PlayerFlagChip playerId={c.player.id} flags={c.player.activeFlags ?? []} />
              <LeaveBadge candidate={c} />
              <StreakBadges candidate={c} />
              <RecentEventsChip events={c.metrics.recentEvents ?? []} />
              <span className="ml-auto flex items-center gap-2">
                <span
                  className="tabular-nums text-[10px] text-slate-500"
                  title="Pure M1 power rank"
                >
                  M1 #{m1Rank}
                </span>
                <span className="tabular-nums text-xs text-slate-400" title="M1 Power">
                  {formatPower(c.latestSnapshot?.m1Power)}
                </span>
                <span className="tabular-nums text-xs text-slate-500">
                  ×{bd.streakFactor.toFixed(2)}
                </span>
                <span
                  className="rounded border border-slate-700 bg-slate-800/70 px-1 py-0.5 text-[10px] font-semibold tabular-nums text-slate-200"
                  title={`Score ${formatScore(bd.score)} — streak ×${bd.streakFactor.toFixed(2)}`}
                >
                  {formatScore(bd.score)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function PolicyField({
  label,
  kind,
  value,
  onChange,
}: {
  label: string;
  kind: 'percent' | 'factor';
  value: number;
  onChange: (v: number) => void;
}) {
  const step = kind === 'percent' ? 0.01 : 0.05;
  const displayValue = kind === 'percent' ? (value * 100).toFixed(0) : value.toFixed(2);
  const suffix = kind === 'percent' ? '%' : '×';
  return (
    <label className="grid grid-cols-[1fr_auto] items-center gap-2 text-sm">
      <span className="text-slate-300">{label}</span>
      <span className="inline-flex items-center gap-1">
        <input
          type="number"
          step={kind === 'percent' ? 1 : step}
          value={displayValue}
          onChange={(e) => {
            const raw = Number(e.target.value);
            if (Number.isNaN(raw)) return;
            onChange(kind === 'percent' ? raw / 100 : raw);
          }}
          className="w-20 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-right text-sm tabular-nums"
        />
        <span className="text-xs text-slate-500">{suffix}</span>
      </span>
    </label>
  );
}
